import { Avatar, Box, CircularProgress, Divider, Link, Stack, Typography, useTheme } from "@mui/material";
import { IconButton, Tooltip } from "@itk/mui-components";
import { useContactPageT } from "src/translation-hooks/contact-page";
import { employeeActions } from "./contact-info-store";
import { connect } from "react-redux";
import { RootState } from "src/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { hasValue, hasValueAndNotEmpty } from "@itk/typescript-utils";
import { CustomerPortalSalesPersonGet, EmployeeGet } from "./contact-info-types";
import { useGetCustomerDetails } from "src/hooks";
import { DekoxIcon } from "src/components";
import { formatPhoneNumber } from "src/utils";
import { Shared_CustomerPortalShowAsContactPersonToCustomerModes } from "src/backend/openapi/v1";

const ownActions = { getEmploeeRequest: employeeActions.getEmployeeRequest, getCustomerPortalContactRequest: employeeActions.getCustomerPortalContactRequest };

const mapStateToProps = (state: RootState) => ({
  employeeDetails: state.pageReducer.employeeInformationReducer.employeedetails,
  contact: state.pageReducer.employeeInformationReducer.contact,
  loadingEmployeesPictureCache: state.pageReducer.employeeInformationReducer.loadingEmployeesPictureCache
});

type Props = ReturnType<typeof mapStateToProps> & typeof ownActions;

export const ContactInfoBase = (props: Props) => {
  const { employeeDetails, contact, getEmploeeRequest, getCustomerPortalContactRequest, loadingEmployeesPictureCache } = props;
  const t = useContactPageT();
  const theme = useTheme();
  const { customerDetails } = useGetCustomerDetails();

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);

  useEffect(() => {
    if (hasValueAndNotEmpty(customerDetails) && hasValueAndNotEmpty(customerDetails?.responsibleEmployeeId) && !hasValueAndNotEmpty(employeeData))
      getEmploeeRequest(customerDetails.responsibleEmployeeId);
  }, [customerDetails]);

  useEffect(() => {
    if (contact.loading === "not-started" && customerDetails?.customerId) getCustomerPortalContactRequest(customerDetails.customerId);
  }, [contact, customerDetails]);

  useEffect(() => {
    checkScroll(); // on mount
    window.addEventListener("resize", checkScroll); // on resize

    return () => window.removeEventListener("resize", checkScroll);
  }, [contact]);

  const employeeData = useMemo((): EmployeeGet | undefined => (employeeDetails.loading === "has-loaded" ? employeeDetails.data : undefined), [employeeDetails]);
  const isLoading = employeeDetails.loading === "is-loading";

  const salesPersons = useMemo(
    () =>
      contact.loading === "has-loaded"
        ? contact.data.salesPersons.map((item: CustomerPortalSalesPersonGet) => ({
            ...item,
            employeePicture: loadingEmployeesPictureCache[item.employeeId]
          }))
        : [],
    [contact, loadingEmployeesPictureCache]
  );

  const checkScroll = () => {
    const container = scrollRef.current;
    if (container) {
      const { scrollWidth, clientWidth } = container;
      setCanScroll(scrollWidth > clientWidth);
    }
  };

  const handleScroll = (direction: "left" | "right") => {
    const container = scrollRef.current;
    if (container) {
      const scrollAmount = 200;
      container.scrollBy({ left: direction === "left" ? -scrollAmount : scrollAmount, behavior: "smooth" });
    }
  };

  const contactPageData = useMemo(() => {
    return [
      {
        title: t.customerSupport,
        email: "info@deko.dk",
        phone: "+45 43 55 77 11",
        icon: <DekoxIcon icon="contactPhone" />
      }
    ];
  }, [employeeData, t]);

  if (isLoading) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" paddingY={4}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack flex={1} px={1.5}>
      <Stack
        width="100%"
        direction="row"
        spacing={3}
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          py: 2
        }}
      >
        {/* Title */}
        <Typography variant="h2" sx={{ color: theme.palette.customColors.darkGrey }}>
          {t.contactDeko}
        </Typography>
      </Stack>
      <Divider sx={{ borderBottomWidth: 3 }} />

      <Stack mt={2} justifyContent="space-between" direction="row">
        <Stack spacing={5}>
          {contactPageData.map((contact, index) => (
            <Stack key={index} spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ color: theme.palette.customColors.darkGrey }}>
                {contact.icon}

                <Typography variant="body2" sx={{ color: theme.palette.customColors.darkGrey }} fontWeight={600}>
                  {contact.title}
                </Typography>
              </Stack>
              <Stack sx={{ marginLeft: 2, color: theme.palette.customColors.darkGrey }} spacing={1} paddingLeft={4}>
                <Link href={`mailto:${contact.email}`} underline="none" target="_blank">
                  <Typography display="flex" gap={1} alignItems="center" variant="subtitle1">
                    <DekoxIcon fontSize="small" icon="mail" /> {contact.email}
                  </Typography>
                </Link>
                <Link href={`tel:${contact.phone}`} underline="none" target="_blank">
                  <Typography display="flex" gap={1} alignItems="center" variant="subtitle1">
                    <DekoxIcon fontSize="small" icon="call" /> {contact.phone}
                  </Typography>
                </Link>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {/* Contacts */}
      {contact.loading === "has-loaded" && salesPersons.length > 0 && (
        <Stack mt={4}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ color: theme.palette.customColors.darkGrey }}>
            <DekoxIcon icon="contacts" />
            <Typography variant="body2" sx={{ color: theme.palette.customColors.darkGrey }} fontWeight={600}>
              {t.yourContacts}
            </Typography>
          </Stack>
          <Box mt={2} sx={{ position: "relative", width: "100%", height: "100%", px: 3 }}>
            {canScroll && (
              <IconButton
                onClick={() => handleScroll("left")}
                k="arrow-left"
                sx={{
                  position: "absolute",
                  left: -13,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 1
                }}
              >
                <DekoxIcon icon="arrow-left" sx={{ color: !canScroll ? theme.palette.grey[400] : theme.palette.common.black }} />
              </IconButton>
            )}
            <Stack
              ref={scrollRef}
              direction="row"
              alignItems="flex-start"
              gap={3}
              sx={{
                overflowX: "auto",
                scrollBehavior: "smooth",
                py: 2,
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": {
                  display: "none"
                },
                width: "100%",
                height: "100%"
              }}
            >
              {salesPersons
                .filter(item => item.showAsContactPersonToCustomerModeId === Shared_CustomerPortalShowAsContactPersonToCustomerModes.Yes)
                .map(salesPerson => (
                  <Stack key={salesPerson.employeeId} gap={1} justifyContent="center" alignItems="center">
                    <Box sx={{ position: "relative", width: 100, height: 100 }}>
                      <Avatar
                        src={hasValue(salesPerson.employeePicture) ? `data:image/jpg;base64,${salesPerson.employeePicture}` : undefined}
                        sx={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "none",
                          backgroundColor: theme.palette.primary.dark
                        }}
                      >
                        {salesPerson.employeeFirstName.charAt(0)}
                      </Avatar>
                    </Box>

                    <Tooltip title={salesPerson.employeeFullName} placement="top">
                      <Typography
                        variant="h6"
                        textAlign="center"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          color: theme.palette.customColors.darkGrey
                        }}
                      >
                        {salesPerson.employeeFullName}
                      </Typography>
                    </Tooltip>
                    <Typography
                      textAlign="center"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        color: theme.palette.customColors.darkGrey
                      }}
                    >
                      {salesPerson.employeeRole}
                    </Typography>

                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      <Link
                        href={`mailto:${salesPerson.employeeEmail}`}
                        underline="none"
                        target="_blank"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "100%",
                          justifyContent: "center"
                        }}
                      >
                        <DekoxIcon icon="mail" fontSize="small" color="primary" />
                        {salesPerson.employeeEmail}
                      </Link>
                    </Box>
                    {hasValueAndNotEmpty(salesPerson.employeePhoneNo) && (
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                        <Link
                          href={`tel:${salesPerson.employeePhoneNo}`}
                          underline="none"
                          target="_blank"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            justifyContent: "center",
                            height: 28
                          }}
                        >
                          <DekoxIcon icon="call" fontSize="small" />
                          {formatPhoneNumber(salesPerson.employeePhoneNo)}
                        </Link>
                      </Box>
                    )}
                  </Stack>
                ))}
            </Stack>
            {canScroll && (
              <IconButton
                onClick={() => handleScroll("right")}
                k="arrow-right"
                sx={{
                  position: "absolute",
                  right: -13,
                  top: "50%",
                  transform: "translateY(-50%)"
                }}
              >
                <DekoxIcon icon="arrow-right" sx={{ color: !canScroll ? theme.palette.grey[400] : theme.palette.common.black }} />
              </IconButton>
            )}
          </Box>
        </Stack>
      )}
    </Stack>
  );
};
export const ContactInfo = connect(mapStateToProps, ownActions)(ContactInfoBase);
